<script setup>
import { useForm } from '@inertiajs/vue3';
import AppButton from '../../../../../components/AppButton.vue';
import SlideOver from '../../../../../components/SlideOver.vue';
import { useModal } from '../../../../../composables/useModal';
import IntegerPlusMinusInput from '../../../../../components/Forms/IntegerPlusMinusInput.vue';
import { computed } from 'vue';
import AnimatedNumber from '../../../../../components/AnimatedNumber.vue';
import SubmitFormButton from '../../../../Teacher/Activities/Subactivities/SubmitFormButton.vue';
import FormError from '../../../../../components/Forms/FormError.vue';

const props = defineProps({
    weapon: {
        type: Object,
    },
    set: {
        type: Object,
    },
    gamificationScoreCard: {
        type: Object,
    },
    action: {
        type: Object,
    },
});

const form = useForm({
    gamification_item_id: props.weapon.action.gamification_item_id,
    boost: 0,
});

const submit = () => {
    form.transform((data) => {
        data.energy = energyCost.value;
        return data;
    }).patch(props.weapon.action.url, {
        preserveScroll: true,
        onSuccess: () => {
            dismiss();
        },
    });
};

const boostedDamage = computed(() => {
    const modifier = 1 + form.boost * props.weapon.boost_modifier;

    const minDamage = Math.round(props.weapon.min_damage * modifier);

    const maxDamage = Math.round(props.weapon.max_damage * modifier);

    return {
        minDamage,
        maxDamage,
    };
});

const energyCost = computed(() => {
    return form.boost * props.weapon.boost_cost;
});

const notEnoughEnergy = computed(() => {
    return energyCost.value > props.gamificationScoreCard.energy_total;
});

const isDisabled = computed(() => {
    if (!props.weapon.can_boost) {
        return false;
    }
    return notEnoughEnergy.value;
});

const { isOpen, dismiss, toggle } = useModal(false);

defineOptions({
    name: 'UseWeaponSlideOverWidget',
});
</script>

<template>
    <AppButton element="button" type="button" v-if="weapon.can.use" @click="toggle">
        {{ weapon.action.text }}
    </AppButton>
    <SlideOver :show="isOpen" @close="dismiss" :title="weapon.name">
        <div>
            <img :src="weapon.image_path" alt="" />
        </div>

        <div class="mt-5">
            <form :action="weapon.action.url" method="post" @submit.prevent="submit">
                <div id="boost_section" v-if="weapon.can_boost">
                    <h2 class="text-lg">{{ $t('messages.do_you_want_to_boost_the_weapon?') }}</h2>
                    <div>{{ $t('messages.weapon_boost_max_is_X_times', { times: weapon.max_boost }) }}</div>
                    <div>{{ $t('messages.weapon_boost_cost', { energy: weapon.boost_cost }) }}</div>
                    <div class="flex justify-between">
                        <div>
                            {{ $t('messages.boost_weapon') }}
                            <span class="font-mono">{{ form.boost }} / {{ weapon.max_boost }}</span>
                        </div>
                        <div>
                            <IntegerPlusMinusInput
                                :min="0"
                                :max="weapon.max_boost"
                                v-model="form.boost"
                                :display-current="false"
                            />
                        </div>
                    </div>
                    <div>
                        {{ $t('messages.energy_cost') }}:
                        <span
                            :class="{
                                'text-red-500': notEnoughEnergy,
                            }"
                            class="font-mono"
                        >
                            <AnimatedNumber :number="energyCost" /> /
                            <span>{{ gamificationScoreCard.energy_total }}</span>
                        </span>
                    </div>
                </div>
                <FormError :value="form.errors.energy" class="mt-2" />
                <div>
                    {{ $t('messages.damage') }}:
                    <AnimatedNumber :number="boostedDamage.minDamage" class="font-mono" /> -
                    <AnimatedNumber :number="boostedDamage.maxDamage" class="font-mono" />
                </div>
                <div class="mt-5 flex justify-end">
                    <SubmitFormButton
                        :button-text="weapon.action.text"
                        :processing="form.processing"
                        :disabled="isDisabled"
                    />
                </div>
            </form>
        </div>
    </SlideOver>
</template>
